/*
  AdministratorRequest.js - Networking File

  Contains networking functionality for Customer_Contact (Adminstrator) actions.

  Author: Kyle Combeer (2020)
  Company: Virtual Ark
*/

import { useCallback } from 'react';
import config from '../Config';
import RequestRabbit from './RequestRabbit';

const useAdministratorRequest = () => {
  const request = useCallback(async (url, options, errorHandler) => {
    try {
      const response = await RequestRabbit.request(url, options, errorHandler);
      return await response?.json();
    } catch (e) {
      errorHandler?.(e.message);
    }
  }, []);

  const getAdministrators = async (
    letter,
    pageNumber,
    pageLength,
    errorHandler,
    searchParams = {}
  ) => {
    try {
      const hasSearchParams = Object.values(searchParams).some(
        (val) => val && val.trim && val.trim()
      );

      let url;
      if (hasSearchParams) {
        url = `${config.baseServerURL}/contact/search`;
        const response = await RequestRabbit.request(
          url,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: searchParams.name?.trim(),
              email: searchParams.email?.trim(),
              contactType: searchParams.contactType,
              pageNumber,
              pageLength,
            }),
          },
          errorHandler
        );
        return await response?.json();
      }

      // Use regular administrators endpoint when no search params
      url = `${config.baseServerURL}/contact?letter=${letter}&pageNumber=${pageNumber}&pageLength=${pageLength}`;
      const response = await RequestRabbit.request(
        url,
        { method: 'GET' },
        errorHandler
      );
      return await response?.json();
    } catch (e) {
      console.error('Error in getAdministrators:', e);
      errorHandler?.(e.message);
      return { items: [], rowCount: 0, letters: [] };
    }
  };

  const getSingleAdministrator = async (contactId, errorHandler) => {
    return request(
      `${config.baseServerURL}/contact/${contactId}`,
      { method: 'GET' },
      errorHandler
    );
  };

  const createAdministrator = async (data, errorHandler) => {
    return request(
      `${config.baseServerURL}/contact`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
      errorHandler
    );
  };

  const editAdministrator = async (customerContactId, data, errorHandler) => {
    return request(
      `${config.baseServerURL}/contact/${customerContactId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
      errorHandler
    );
  };

  const deleteAdministrator = async (customerContactId, errorHandler) => {
    return request(
      `${config.baseServerURL}/contact/${customerContactId}`,
      { method: 'DELETE' },
      errorHandler
    );
  };

  const getExtras = async (errorHandler) => {
    return request(
      `${config.baseServerURL}/contact/extras`,
      { method: 'GET' },
      errorHandler
    );
  };

  const getUserByEmail = async (email, errorHandler) => {
    return request(
      `${config.baseServerURL}/contact/user`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_email: email }),
      },
      errorHandler
    );
  };

  return {
    getAdministrators,
    getSingleAdministrator,
    createAdministrator,
    editAdministrator,
    deleteAdministrator,
    getExtras,
    getUserByEmail,
  };
};

export default useAdministratorRequest;
